import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { WorkflowObjectType, WorkflowType } from '@/client/services/api/graphql/gql/graphql';

import type { AssignmentFormData } from '../../../types/assignmentTypes';
import ContentSearch from '@/client/components/admin/shared/search/ContentSearch';
import { DefaultTFuncReturn } from 'i18next';
import SolidButton from '@/client/components/shared/buttons/SolidButton';
import { useContentSearch } from '@/client/components/admin/shared/search/useContentSearch';
import { useDebounceValue } from 'usehooks-ts';
import { useFormContext } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TriggerObjectSelectionModalProps {
  title: string | DefaultTFuncReturn;
  isOpen: boolean;
  onClose: () => void;
  workflowType: WorkflowType;
  workshopsOnly?: boolean;
}

export default function TriggerObjectSelectionModal({
  title,
  isOpen,
  onClose,
  workflowType,
  workshopsOnly = false,
}: TriggerObjectSelectionModalProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useDebounceValue('', 300);
  const { contentTypeOptions } = useContentSearch();
  const [selectedContentType, setSelectedContentType] = useState<{
    name: string;
    value: string | null;
  } | null>(
    workshopsOnly
      ? { name: t('contentSearch.workshops'), value: 'workshops' }
      : contentTypeOptions[0],
  );

  const { setValue, watch, clearErrors } = useFormContext<AssignmentFormData>();
  const { settings } = watch();
  const { triggerObject } = settings;

  const hasTriggerObject = !!triggerObject?.id && !!triggerObject?.title;

  const handleItemClick = (item: any, isSelected: boolean | undefined) => {
    if (isSelected) {
      setValue('settings.triggerObject', { id: null, title: '' });
      return;
    }

    const objTypeMap: Record<string, WorkflowObjectType> = {
      article: WorkflowObjectType.Article,
      video: WorkflowObjectType.Video,
      file: WorkflowObjectType.File,
      scorm: WorkflowObjectType.Scorm,
      assessment: WorkflowObjectType.Assessment,
      track: WorkflowObjectType.Track,
      journey: WorkflowObjectType.Journey,
      imported_content: WorkflowObjectType.ImportedContent,
      workshop: WorkflowObjectType.Workshop,
      multi_session_workshop: WorkflowObjectType.Workshop,
    };

    setValue('settings.objectType', objTypeMap[item.type]);
    setValue('settings.triggerObject', { id: item.id, title: item.title || item.name });
    setValue('formBoundType', workflowType);
    setValue('settings.type', workflowType);
    clearErrors('settings.triggerObject');
  };

  const checkItemIsSelected = (item: any) => item.id === triggerObject?.id;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>{title}</ModalHeader>

        <ModalBody overflowY="auto" maxHeight="500px">
          <ContentSearch
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedContentType={selectedContentType}
            setSelectedContentType={setSelectedContentType}
            handleItemClick={handleItemClick}
            checkItemIsSelected={checkItemIsSelected}
            includeAdvancedContentTypes
            workshopsOnly={workshopsOnly}
          />
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button variant="createCancel" onClick={onClose}>
              {t('admin.assignments.automations.triggerObjectModal.cancel')}
            </Button>

            <SolidButton isDisabled={!hasTriggerObject} onClick={onClose}>
              {t('admin.assignments.automations.triggerObjectModal.save')}
            </SolidButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
